import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, Inject, inject, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AiKnowledgeService } from '../../ai-knowledge.service';
import { AiKnowledgeI18nModule } from '../../assets/i18n/ai-knowledge-i18n.module';
import { MatCardModule } from '@angular/material/card';
import { CustomDataComponent } from '../custom-data/custom-data.component';
import { WebsiteScrapeComponent } from '../website-scrape/website-scrape.component';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';

import { MatButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogConfig,
  MatDialogContent,
  MatDialogRef,
} from '@angular/material/dialog';

import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ComponentType } from '@angular/cdk/portal';
import { KnowledgeCreatedEvent } from '../common/interfaces';
import { KnowledgeSourceConfig, KnowledgeSourceKey } from '@vendasta/embeddings';

interface Choice {
  title: string;
  description: string;
  component: any;
}

interface Data {
  knowledgeSourceKey?: KnowledgeSourceKey;
  config?: KnowledgeSourceConfig;
  isSuggested?: boolean;
  stepOption?: string;
  excludeAddToAIStep?: boolean;
}

@Component({
  selector: 'ai-knowledge-add-knowledge',
  imports: [
    CommonModule,
    TranslateModule,
    AiKnowledgeI18nModule,
    MatCardModule,
    GalaxyLoadingSpinnerModule,
    MatButton,
    MatDialogActions,
    MatDialogContent,
  ],
  providers: [AiKnowledgeService],
  templateUrl: './add-knowledge.component.html',
  styleUrls: ['./add-knowledge.component.scss'],
})
export class AddKnowledgeComponent implements OnInit {
  private readonly DIALOG_WIDTH = '660px';
  private readonly bpObserver = inject(BreakpointObserver);
  private readonly dialog = inject(MatDialog);
  choices: Choice[] = [
    {
      title: 'EDIT_KNOWLEDGE.CUSTOM_DATA_CONFIG.TITLE',
      description: 'EDIT_KNOWLEDGE.CUSTOM_DATA_CONFIG.DESCRIPTION',
      component: CustomDataComponent,
    },
    {
      title: 'EDIT_KNOWLEDGE.WEBSITE_SCRAPE_CONFIG.TITLE',
      description: 'EDIT_KNOWLEDGE.WEBSITE_SCRAPE_CONFIG.DESCRIPTION',
      component: WebsiteScrapeComponent,
    },
  ];
  componentSelected = new EventEmitter<{ knowledgeCreated: EventEmitter<string>; destroyRef: DestroyRef }>();

  constructor(
    public dialogRef: MatDialogRef<AddKnowledgeComponent>,
    public readonly destroyRef: DestroyRef,
    @Inject(MAT_DIALOG_DATA) public data: Data,
  ) {}

  ngOnInit(): void {
    this.dialogRef.disableClose = true;
  }

  selectComponent(choice: Choice): void {
    const component = this.openDialog(choice.component);
    this.componentSelected.emit({ knowledgeCreated: component.knowledgeCreated, destroyRef: component.destroyRef });
    this.close();
  }

  private openDialog(type: ComponentType<KnowledgeCreatedEvent>): KnowledgeCreatedEvent {
    const conf: MatDialogConfig = {
      width: this.DIALOG_WIDTH,
      data: { excludeAddToAIStep: this.data?.excludeAddToAIStep },
    };

    const isSmallScreen = this.bpObserver.isMatched(Breakpoints.XSmall);
    if (isSmallScreen) {
      conf.maxWidth = '100vw';
      conf.maxHeight = '100vh';
    }
    const editor = this.dialog.open(type, conf);
    return editor.componentInstance;
  }

  close(): void {
    this.dialogRef.close();
  }
}
